// ABOUTME: Modal for uploading the fulfilment logo for the current customer.
// ABOUTME: Calls PUT /customers/logo via useUpdateCustomerFulfilmentLogoMutation to persist the upload.

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import { Box, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { useDeleteCustomerFulfilmentLogoMutation, useGetCustomerLogoQuery, useUpdateCustomerFulfilmentLogoMutation } from "../../../newapi/user/userSlice";
import { selectEnvironmentid } from "../../../newapi/global/settingsSlice";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../utils/toasts";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import Shbutton from "../../theme/buttons/Shbutton";
import SHModal from "../../theme/Modal/SHModal";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import UploadLogoSelector from "../../theme/upload/logo/UploadLogoSelector";
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import MConfirmDelete from "./MConfirmDelete";

const MEditFulfilmentLogo = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [logoFile, setLogoFile] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const selectedEnvironmentId = useSelector(selectEnvironmentid);
    const { data: logoData } = useGetCustomerLogoQuery(selectedEnvironmentId, { skip: !props.open });
    const [updateCustomerFulfilmentLogo, { isLoading }] = useUpdateCustomerFulfilmentLogoMutation();
    const [deleteCustomerFulfilmentLogo, { isLoading: isDeleting }] = useDeleteCustomerFulfilmentLogoMutation();

    const handleClose = () => {
        setLogoFile(null);
        props.handleClose();
    };

    const handleSave = async () => {
        if (!logoFile || isLoading) return;
        try {
            const resp = await updateCustomerFulfilmentLogo(logoFile).unwrap();
            showToastMessageUpdateRequest(t, resp);
            handleClose();
        } catch (err) {
            showToastMessageRequestError(t('upload_failed'), t, err);
        }
    };

    const handleDelete = async () => {
        if (isDeleting) return;
        try {
            const resp = await deleteCustomerFulfilmentLogo().unwrap();
            showToastMessageUpdateRequest(t, resp);
            handleClose();
        } catch (err) {
            showToastMessageRequestError(t('delete_failed'), t, err);
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: 'center',
                }}
            >
                <Subheading>{t("fulfilment_logo")}</Subheading>
                <Text light>{t("fulfilment_logo_description")}</Text>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box>
                    <Box
                        sx={{
                            mb:1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:'space-between',
                            height:32,
                            borderRadius:5,
                            background: colors.orange[100],
                            border:`1px solid ${colors.orange[400]}`,
                            zIndex: 1,
                            px:2,
                            gap:1.5,
                        }}
                    >
                        <WarningAmberOutlinedIcon fontSize="small" sx={{ color: colors.orange[400] }} />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Text align="center" semibold variant="orange">
                                {t("logo_recommended_height_40")}
                            </Text>
                        </Box>
                        <Box sx={{flexShrink:0,width:32,}}></Box>
                    </Box>
                    <UploadLogoSelector
                        title={t("logo")}
                        variant="logo"
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        icon={<BrandingWatermarkOutlinedIcon />}
                        previewUrl={logoData?.logoUrl}
                        onFileSelect={(file) => setLogoFile(file)}
                        onRemove={() => setLogoFile(null)}
                    />
                    {logoData?.logoUrl && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                            <Shbutton
                                block
                                variant="text"
                                color="red"
                                onClick={() => setConfirmDeleteOpen(true)}
                                disabled={isDeleting}
                            >
                                {t("remove_current_logo")}
                            </Shbutton>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    justifyContent: "space-between",
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={() => handleClose()}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleSave}
                        disabled={!logoFile || isLoading}
                    >
                        {t("save")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
            <MConfirmDelete
                open={confirmDeleteOpen}
                handleClose={() => setConfirmDeleteOpen(false)}
                confirm={handleDelete}
                data={t("fulfilment_logo")}
            />
        </SHModal>
    );
};

export default MEditFulfilmentLogo;
